import React, { FormEvent } from 'react';
import {
  DeepPartial,
  Path,
  PathValue,
  UnpackNestedValue,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  FieldValues,
} from 'react-hook-form';

import {
  phoneCodesList,
} from '../../components/FormSelectPhoneCodeCountry/FormSelectPhoneCodeCountry';
import { CountryCode } from '../../types/apiEnums';

type UseCheckboxParams<TFields extends FieldValues,> = {
  key: Path<TFields>;
  setValue: UseFormSetValue<TFields>;
  value: UnpackNestedValue<PathValue<TFields, Path<TFields>>>;
};

type UsePreFillFieldsParams<TFields extends FieldValues,> = {
  getValues: UseFormGetValues<TFields>;
  isDirty: boolean;
  preFillData?: UnpackNestedValue<Partial<TFields>> | null;
  reset: UseFormReset<TFields>;
};

export type UseResetFieldsParams<TFields extends FieldValues,> = {
  defaultValues?: UnpackNestedValue<DeepPartial<TFields> | TFields>;
  reset: UseFormReset<TFields>;
};

export type UseGetPhoneNumberParams = {
  countryCode: CountryCode | null;
  phoneCode: string;
  phoneNumber: string | undefined;
};

export const formHooks = {
  useCheckbox<TFields extends FieldValues,>({
    key,
    value,
    setValue,
  }: UseCheckboxParams<TFields>) {
    return React.useCallback(() => {
      setValue(key, value as never, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }, [key, value, setValue]);
  },

  usePreFillFields<TFields extends FieldValues,>({
    isDirty,
    getValues,
    reset,
    preFillData,
  }: UsePreFillFieldsParams<TFields>) {
    const ref = React.useRef({
      isDirty,
      getValues,
      reset,
    });

    React.useEffect(() => {
      if (!ref.current || ref.current.isDirty || !preFillData) {
        return;
      }

      ref.current.reset({
        ...ref.current.getValues(),
        ...preFillData,
      });

      ref.current.isDirty = true;
    }, [preFillData]);
  },

  usePreventDefaultHandler() {
    return React.useCallback((e: FormEvent<HTMLFormElement>) => e.preventDefault(), []);
  },

  useResetForm<TFields extends FieldValues,>({
    reset,
    defaultValues,
  }: UseResetFieldsParams<TFields>) {
    const handleResetForm = React.useCallback(() => {
      reset(defaultValues as never);
    }, [defaultValues, reset]);

    return { handleResetForm };
  },

  useHandleCountryChange(defaultCountryCode: CountryCode | null = null) {
    const [phoneCode, setPhoneCode] = React.useState<string>(
      defaultCountryCode ? phoneCodesList[defaultCountryCode] : '',
    );
    const [countryCode, setCountryCode] = React.useState<CountryCode | null>(defaultCountryCode);

    const handleCountryChange = React.useCallback((countryCode: CountryCode, phoneCode: string) => {
      setPhoneCode(phoneCode);
      setCountryCode(countryCode);
    }, [setPhoneCode, setCountryCode]);

    return {
      phoneCode,
      countryCode,
      handleCountryChange,
    };
  },

  useGetCountryCode(phoneNumber: string) {
    return React.useMemo(() => {
      if (!phoneNumber.length) return null;

      for (const countryCode in phoneCodesList) {
        const phoneCode = phoneCodesList[countryCode];

        if (phoneNumber.startsWith(phoneCode)) {
          return countryCode as CountryCode;
        }
      }
      return null;
    }, [phoneNumber]);
  },

  useGetPhoneNumber({
    countryCode,
    phoneCode,
    phoneNumber,
  }: UseGetPhoneNumberParams) {
    return React.useMemo(() => {
      return countryCode && phoneNumber
        ? phoneNumber.slice(phoneCode.length)
        : phoneNumber;
    },
    [countryCode, phoneCode, phoneNumber]);
  },
};
